/* eslint-disable complexity */
'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import toast from 'react-hot-toast';
import { Menu } from 'lucide-react';

import {
  useAllModelQuery,
  useAllStateQuery,
  useHomeFilterQuery,
  useListingsQuery,
} from '@/slw/entities/search-results/search-results.query';
import { ListingsFilter } from '@/slw/entities/search-results/search-results.type';
import { isLoggedInClient } from '@/slw/shared/lib/auth.client';
import { ClientStorage } from '@/slw/shared/lib/client-storage';
import { useResponsive } from '@/slw/shared/hooks/use-responsive';
import { Sheet, SheetContent, SheetTrigger } from '@/slw/shared/ui/sheet';
import { formatNumberInput } from '@/slw/shared/ui/form/input';
import { HomeFilterType } from '@/slw/entities/home/home.model';

import { FilterSidebarTab } from './filter-sidebar.ui';
import { ListingSummarySortTab } from './listing-summary-sort.ui';
import { SearchResultGrid } from './search-result-grid.ui';
import { Pagination } from '../common/pagination';
import { SaveSearchModal } from './save-your-search.modal';
import { TopPage } from './top-page.ui';
import { Filtered } from './filtered.ui';

function MainSearchResultsComponent() {
  const searchParamsFE = useSearchParams();
  const params = useParams();
  const searchParams = Object.fromEntries(searchParamsFE.entries());
  const isLogged = isLoggedInClient();
  const router = useRouter();
  const topRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const storeSetValue = useRef<(...args: any) => void>((...args: any) => {});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const storeResetForm = useRef<(...args: any) => void>((values: any) => {});
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
  const { slug } = params;

  // Parse initial state from URL
  const filter: ListingsFilter = useMemo(() => {
    const price = searchParams.price;
    let fromPrice = undefined;
    let toPrice = undefined;

    if (price) {
      if (price.startsWith('>=') && Number(price.split('>=')[1])) {
        fromPrice = Number(price.split('>=')[1]) > 3000000 ? 3000000 : Number(price.split('>=')[1]);
      } else if (price.startsWith('<=') && Number(price.split('<=')[1])) {
        toPrice = Number(price.split('<=')[1]) < 0 ? 0 : Number(price.split('<=')[1]);
      } else if (price.startsWith('>') && Number(price.split('>')[1])) {
        fromPrice = Number(price.split('>')[1]) + 1 > 3000000 ? 3000000 : Number(price.split('>')[1]) + 1;
      } else if (price.startsWith('<') && Number(price.split('<')[1])) {
        toPrice = Number(price.split('<')[1]) - 1 < 0 ? 0 : Number(price.split('<')[1]) - 1;
      } else if (price.startsWith('=') && Number(price.split('=')[1])) {
        fromPrice = Number(price.split('=')[1]);
      } else if (Number(price.split('=')[1])) {
        fromPrice = Number(price.split('=')[1]);
      }
    }

    let ftStore = searchParams.ft;
    const ftSession = ClientStorage.getSessionStorage('filter');

    let ft = {} as ListingsFilter;

    try {
      if (ftSession) {
        ClientStorage.removeSessionStorage('filter');
        ft = ftSession as ListingsFilter;
      } else if (ftStore) {
        ft = JSON.parse(decodeURIComponent(ftStore));
      }
    } catch {}

    return {
      toYear: ft.toYear ? `${ft.toYear}` : undefined,
      fromPrice: ft.fromPrice ? `${formatNumberInput(ft.fromPrice)}` : fromPrice ? `${fromPrice}` : undefined,
      toPrice: ft.toPrice ? `${formatNumberInput(ft.toPrice)}` : toPrice ? `${fromPrice}` : undefined,
      fromOdometer: ft.fromOdometer ? `${formatNumberInput(ft.fromOdometer)}` : undefined,
      toOdometer: ft.toOdometer ? `${formatNumberInput(ft.toOdometer)}` : undefined,
      transmission: ft.transmission || undefined,
      engineHistory: ft.engineHistory || undefined,
      engineCondition: ft.engineCondition || undefined,
      seatMaterial: ft.seatMaterial || undefined,
      sortBy: ft.sortBy || undefined,
      pageIndex: ft.pageIndex ? Number(ft.pageIndex) : 1,
      pageSize: ft.pageSize ? Number(ft.pageSize) : 18,

      // Sync from url
      orBestOffer:
        (ft.orBestOffer && Boolean(ft.orBestOffer)) ?? (searchParams.orBestOffer === 'true' ? true : undefined),
      premiumSpotlight:
        (ft.premiumSpotlight && Boolean(ft.premiumSpotlight)) ??
        (searchParams.premiumSpotlight === 'true' ? true : undefined),
      keyword: ft.keyword ?? (searchParams.keyword || undefined),
      filterByTag: ft.filterByTag ?? (searchParams.filterByTag ? [searchParams.filterByTag] : undefined),
      fromYear: ft.fromYear ?? (slug?.[0] === 'all-year' ? undefined : slug?.[0] || undefined),
      country: ft.country ?? (searchParams.country ? [searchParams.country] : undefined),
      state: ft.state ?? (searchParams.state ? [searchParams.state] : undefined),
      make: ft.make ?? (slug?.[1] ? [slug?.[1]] : undefined),
      model: ft.model ?? (slug?.[2] ? [slug?.[2]] : undefined),
    };
  }, [searchParams, slug]);

  // const [filter, setFilter] = useState<ListingsFilter>(initialFilter);
  const [saveModalOpen, setSaveModalOpen] = useState(false);

  const { isMaxSm } = useResponsive();

  const { data, isLoading, error } = useListingsQuery({ ...filter });
  const allModel = useAllModelQuery();
  const allState = useAllStateQuery();
  const homeFilterQuery = useHomeFilterQuery();

  const homeFilter = homeFilterQuery?.data?.data || ({} as HomeFilterType);

  useEffect(() => {
    // setFilter(initialFilter);

    if (searchParams.defaultFt && Number(searchParams.defaultFt) === 1) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { sortBy, pageIndex, pageSize, ...ftReset } = { ...filter };

      storeResetForm.current({ ...ftReset });
    }
  }, [filter, searchParams.defaultFt]);

  const listings = data?.data ?? [];
  const totalCount = data?.metaData?.totalItems ?? 0;
  const pageSize = data?.metaData?.pageSize ?? 1;
  // Calculate summary range
  const rangePage = useMemo(() => {
    return {
      start: totalCount === 0 ? 0 : (Number(filter.pageIndex) - 1) * pageSize + 1,
      end: Math.min(Number(filter.pageIndex) * pageSize, totalCount),
    };
  }, [filter.pageIndex, pageSize, totalCount]);
  // const start = totalCount === 0 ? 0 : (Number(filter.pageIndex) - 1) * pageSize + 1;
  // const end = Math.min(Number(filter.pageIndex) * pageSize, totalCount);

  const replaceUrl = useCallback(
    (url: string, formValues: ListingsFilter) => {
      ClientStorage.setSessionStorage('filter', { ...filter, ...formValues });

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        router.push(`${url}?ft=${encodeURIComponent(JSON.stringify({ ...filter, ...formValues }))}`, { scroll: false });
      }, 100);
    },
    [filter, router],
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const getHeadingTitle = useCallback(() => {
    let title = '';

    if (!allModel.data?.data) {
      return '';
    }

    const make = slug?.[1] ? slug?.[1] : undefined;
    const model = slug?.[2] ? slug?.[2] : undefined;

    if (make) {
      title = allModel.data.data.find((item) => item.makeSlug === make)?.make || '';
    }

    if (model) {
      const modelName = allModel.data.data.find((item) => item.modelSlug === model)?.model || '';

      title = title ? `${title} ${modelName}` : modelName;
    }

    return title;
  }, [allModel?.data?.data, slug]);

  const onSaveSearch = useCallback(() => {
    if (!isLogged) {
      return toast.error('Please login or sign up to save your search');
    }

    setSaveModalOpen(true);
  }, [isLogged]);

  const clearFiltered = useCallback(
    (type: keyof typeof filter, clearValue: any) => {
      if (!storeSetValue.current) return;

      switch (type) {
        case 'keyword':
          storeSetValue.current(type, '');
          break;
        case 'filterByTag':
        case 'country':
        case 'state':
        case 'make':
        case 'model':
        case 'transmission':
        case 'engineHistory':
        case 'engineCondition':
        case 'seatMaterial': {
          const value = (filter[type] as any).filter((item: string) => item !== clearValue);

          storeSetValue.current(type, value);
          break;
        }
        case 'premiumSpotlight':
        case 'orBestOffer':
          storeSetValue.current(type, false);
          break;
        case 'fromYear':
        case 'toYear':
        case 'fromPrice':
        case 'toPrice':
        case 'fromOdometer':
        case 'toOdometer':
          storeSetValue.current(type, '');
          break;

        default:
          break;
      }
    },
    [filter],
  );

  const onSortChange = useCallback(
    (v: string) => {
      const sortBy = v === 'best_match' ? undefined : v;
      const year = Number(filter.fromYear) ? Number(filter.fromYear) : 'all-year';

      replaceUrl(`/search-results/${year}`, { sortBy });
    },
    [filter.fromYear, replaceUrl],
  );

  const onPageChange = useCallback(
    (pageIndex: number) => {
      const year = Number(filter.fromYear) ? Number(filter.fromYear) : 'all-year';

      replaceUrl(`/search-results/${year}`, { pageIndex });
    },
    [filter.fromYear, replaceUrl],
  );

  return (
    <>
      <TopPage title={getHeadingTitle()} setSaveModalOpen={setSaveModalOpen} isLogged={isLogged} isMaxSm={isMaxSm} />

      {isMaxSm && (
        <div className='flex items-center justify-between'>
          <Sheet>
            <SheetTrigger asChild>
              <button
                type='button'
                className='relative font-bold rounded-[6px] bg-primary text-background uppercase
                            flex items-center gap-1 md:gap-10 px-4 md:px-5 py-2 md:py-[0.75rem]
                            hover:text-background! hover:bg-primary-3 transition-all duration-300'
              >
                Filter <Menu size={17} color='#fff' />
              </button>
            </SheetTrigger>
            <SheetContent className='w-4/5'>
              <div className='overflow-y-auto max-h-screen p-0 mt-4'>
                <div className='max-h-[calc(100vh-70px)]'>
                  <aside className='lg:shrink-0 w-full overflow-hidden'>
                    <FilterSidebarTab
                      filter={filter}
                      // setFilter={setFilter}
                      allModel={allModel}
                      allState={allState}
                      homeFilter={homeFilter}
                      storeSetValue={storeSetValue}
                      storeResetForm={storeResetForm}
                      replaceUrl={replaceUrl}
                    />
                  </aside>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <div>
            <button
              type='button'
              onClick={onSaveSearch}
              className='relative font-bold rounded-[6px] bg-primary text-background uppercase
                    flex items-center gap-1 md:gap-10 px-4 md:px-5 py-2 md:py-[0.75rem]
                    hover:text-background! hover:bg-primary-3 transition-all duration-300'
            >
              <span className='whitespace-nowrap'>Save your Search</span>
              <Image src='/images/icon/plus-white.png' width={17} height={17} alt='' />
            </button>
          </div>
        </div>
      )}

      <main className='mt-10 flex flex-col md:flex-row gap-2 md:gap-4 lg:gap-6  mb-20' ref={topRef}>
        {!isMaxSm && (
          <aside className='lg:shrink-0 xl:max-w-[20rem] md:w-2/7 lg:w-1/3 w-full overflow-hidden'>
            <FilterSidebarTab
              filter={filter}
              // setFilter={setFilter}
              allModel={allModel}
              allState={allState}
              homeFilter={homeFilter}
              storeSetValue={storeSetValue}
              storeResetForm={storeResetForm}
              replaceUrl={replaceUrl}
            />
          </aside>
        )}
        <div className='flex-1'>
          <ListingSummarySortTab
            start={rangePage.start}
            end={rangePage.end}
            totalCount={totalCount}
            sort={filter.sortBy}
            onSortChange={onSortChange}
          />

          <Filtered
            filter={filter}
            homeFilter={homeFilter}
            allState={allState}
            allModel={allModel}
            clearFiltered={clearFiltered}
          />

          <SearchResultGrid listings={listings} loading={isLoading} error={error} />

          {!!listings && !!listings.length && (
            <Pagination
              className='mt-6'
              currentPage={filter.pageIndex}
              pageSize={filter.pageSize}
              totalItems={totalCount}
              onPageChange={(data) => {
                onPageChange(data.page);
                requestAnimationFrame(() => {
                  topRef.current && topRef.current?.scrollIntoView({ behavior: 'smooth' });
                });
              }}
            />
          )}
        </div>

        {isLogged && saveModalOpen && (
          <SaveSearchModal filter={filter} open={saveModalOpen} onClose={() => setSaveModalOpen(false)} />
        )}
      </main>
    </>
  );
}

export const MainSearchResults = React.memo(MainSearchResultsComponent);
